import React, { useMemo, useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { ExternalLinkIcon, DiffIcon } from "lucide-react";
import moment from "moment";
import { TestRunRequest, ToolCall } from "../../model_gen/evaluation";
import { RequestViewButton } from "../shared/button/Buttons";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";
import { cn } from "../../lib/utils";
import { getMessageContentText } from "../../utils/FormatUtils";

// Custom Score Tooltip Component
interface ScoreTooltipProps {
  children: React.ReactNode;
  score: number;
  results: Array<{
    criteria: string;
    score_value: number;
    description?: string;
  }>;
}

const ScoreTooltip: React.FC<ScoreTooltipProps> = ({ children, score, results }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getScoreLabel = (score: number) => {
    if (score >= 90) return { label: "EXCELLENT", color: "#52c41a" };
    if (score >= 75) return { label: "GREAT", color: "#b7eb8f" };
    if (score >= 60) return { label: "GOOD", color: "#d9d9d9" };
    if (score >= 40) return { label: "FAIR", color: "#ffa39e" };
    return { label: "POOR", color: "#f5222d" };
  };

  const scoreLabel = getScoreLabel(score);
  const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 384; // w-96 = 384px
    const viewportWidth = window.innerWidth;
    const margin = 16; // Safety margin from screen edges

    // Calculate the ideal center position
    const idealLeft = containerRect.left + containerRect.width / 2;
    
    // Calculate how much space we need on each side
    const halfTooltipWidth = tooltipWidth / 2;
    
    // Check if tooltip would overflow on the left
    if (idealLeft - halfTooltipWidth < margin) {
      // Position tooltip flush with left margin
      const leftOffset = margin - (idealLeft - halfTooltipWidth);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% + ${leftOffset}px))`
      });
    }
    // Check if tooltip would overflow on the right
    else if (idealLeft + halfTooltipWidth > viewportWidth - margin) {
      // Position tooltip flush with right margin
      const rightOffset = (idealLeft + halfTooltipWidth) - (viewportWidth - margin);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% - ${rightOffset}px))`
      });
    }
    // Tooltip fits perfectly centered
    else {
      setPosition({
        left: "50%",
        transform: "translateX(-50%)"
      });
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after the tooltip is visible
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on window resize
  React.useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVisible]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="absolute top-full mt-2 z-[9999] pointer-events-none"
          style={{
            left: position.left,
            transform: position.transform
          }}
        >
          <div 
            ref={tooltipRef}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-96 max-w-md"
          >
            {/* Tooltip arrow */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white dark:border-b-gray-800"></div>
            
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: scoreLabel.color }}
                />
                <span className="font-semibold text-foreground text-sm">
                  Overall Score
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xl font-bold text-foreground">{score}%</span>
                <span 
                  className="text-xs font-medium px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: `${scoreLabel.color}20`,
                    color: scoreLabel.color 
                  }}
                >
                  {scoreLabel.label}
                </span>
              </div>
            </div>

            {/* Criteria Breakdown */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                CRITERIA BREAKDOWN
              </div>
              {sortedResults.map((result, index) => (
                <div key={index} className="space-y-1">
                  {/* Use a grid layout to ensure proper spacing */}
                  <div className="grid grid-cols-[1fr,auto] gap-3 items-start">
                    <div className="min-w-0"> {/* min-w-0 allows text to shrink */}
                      <span className="text-sm font-medium text-foreground break-words">
                        {result.criteria}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Star rating visual - flex-shrink-0 prevents compression */}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div
                            key={star}
                            className={cn(
                              "w-3 h-3 rounded-sm flex-shrink-0",
                              star <= result.score_value
                                ? "bg-maitai-green"
                                : "bg-gray-200 dark:bg-gray-600"
                            )}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground ml-1 whitespace-nowrap">
                        {result.score_value}/5
                      </span>
                    </div>
                  </div>
                  {result.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed pl-1 break-words">
                      {result.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Responses Tooltip Component  
interface ResponsesTooltipProps {
  children: React.ReactNode;
  record: TestRunRequest;
}

const ResponsesTooltip: React.FC<ResponsesTooltipProps> = ({ children, record }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const renderToolCalls = (toolCalls: ToolCall[]) => {
    return toolCalls.map((toolCall: ToolCall) => (
      <div key={toolCall.id} className="text-xs text-muted-foreground mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border-l-2 border-maitai-green">
        <div className="font-medium text-foreground">{toolCall.function.name}</div>
        <div className="font-mono text-xs">{toolCall.function.arguments}</div>
      </div>
    ));
  };

  if (!record.original_request) {
    return <>{children}</>;
  }

  const originalResponse =
    getMessageContentText(
      record.original_request.metadata?._maitai_test_request_response as
        | string
        | { [k: string]: unknown }[]
        | null
        | undefined
    ) ||
    getMessageContentText(
      record.original_request?.params?.messages?.[
        record.original_request?.params?.messages?.length - 1
      ]?.content
    ) ||
    "No response";

  const testResponse =
    getMessageContentText(record.response?.choices?.[0]?.message?.content) ||
    "No response";

  const originalToolCalls = 
    (record.original_request.metadata?._maitai_test_request_response_tool_calls as ToolCall[]) ||
    (record.original_request?.params?.messages?.[
      record.original_request?.params?.messages?.length - 1
    ]?.tool_calls as ToolCall[]) || [];

  const testToolCalls = 
    (record.response?.choices?.[0]?.message?.tool_calls as ToolCall[]) || [];

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 900; // Our tooltip width
    const viewportWidth = window.innerWidth;
    const margin = 16; // Safety margin from screen edges

    // Calculate the ideal center position
    const idealLeft = containerRect.left + containerRect.width / 2;
    
    // Calculate how much space we need on each side
    const halfTooltipWidth = tooltipWidth / 2;
    
    // Check if tooltip would overflow on the left
    if (idealLeft - halfTooltipWidth < margin) {
      // Position tooltip flush with left margin
      const leftOffset = margin - (idealLeft - halfTooltipWidth);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% + ${leftOffset}px))`
      });
    }
    // Check if tooltip would overflow on the right
    else if (idealLeft + halfTooltipWidth > viewportWidth - margin) {
      // Position tooltip flush with right margin
      const rightOffset = (idealLeft + halfTooltipWidth) - (viewportWidth - margin);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% - ${rightOffset}px))`
      });
    }
    // Tooltip fits perfectly centered
    else {
      setPosition({
        left: "50%",
        transform: "translateX(-50%)"
      });
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after the tooltip is visible
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on window resize
  React.useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVisible]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="absolute bottom-full mb-2 z-[9999] pointer-events-none"
          style={{
            left: position.left,
            transform: position.transform
          }}
        >
          <div 
            ref={tooltipRef}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-6 w-[900px] max-w-[900px]"
          >
            {/* Header */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <DiffIcon className="w-4 h-4 text-maitai-green" />
              <span className="font-semibold text-foreground">Response Comparison</span>
            </div>

            {/* Two-column comparison */}
            <div className="grid grid-cols-2 gap-6">
              {/* Original Response */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium text-foreground text-sm">
                    Original Response
                    {record.original_request.metadata?._maitai_test_request_response && (
                      <span className="text-maitai-green ml-1">*</span>
                    )}
                  </span>
                </div>
                <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                  <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                    {originalResponse}
                  </div>
                  {originalToolCalls.length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium text-foreground text-xs mb-2">Tool Calls:</div>
                      {renderToolCalls(originalToolCalls)}
                    </div>
                  )}
                </div>
              </div>

              {/* Test Response */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-maitai-green rounded-full"></div>
                  <span className="font-medium text-foreground text-sm">Test Response</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                  <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                    {testResponse}
                  </div>
                  {testToolCalls.length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium text-foreground text-xs mb-2">Tool Calls:</div>
                      {renderToolCalls(testToolCalls)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legend note */}
            {record.original_request.metadata?._maitai_test_request_response && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs text-muted-foreground">
                  <span className="text-maitai-green">*</span> Indicates edited/corrected response from test set
                </div>
              </div>
            )}

            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white dark:border-t-gray-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

interface TestRunRequestComparisonTableProps {
  data: TestRunRequest[];
  loading: boolean;
  sortConfig?: SortConfig<TestRunRequest>;
  onSort?: (key: keyof TestRunRequest) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
  apiKey: ApiKeyOrGetter;
  testSetId?: string;
  systemPrompt?: string;
  intentGroupId?: string;
  testRunId: number;
  testRunRequestId: number;
  onRowClick?: (record: TestRunRequest) => void;
}

const TestRunRequestComparisonTable: React.FC<TestRunRequestComparisonTableProps> = ({
  data,
  loading,
  sortConfig,
  onSort,
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
  apiKey,
  testSetId,
  systemPrompt,
  intentGroupId,
  testRunId,
  testRunRequestId,
  onRowClick,
}) => {


  const columns: PaginatedTableColumn<TestRunRequest>[] = useMemo(() => [
    {
      key: "test_run_id",
      header: "Test Run",
      width: "w-[10%]",
      sortable: true,
      render: (record: TestRunRequest) => (
        <div className="text-sm font-gc-reg text-foreground">
          #{record.test_run_id}
        </div>
      ),
    },
    {
      key: "date_executed",
      header: "Date",
      width: "w-[20%]",
      sortable: true,
      render: (record: TestRunRequest) => {
        if (!record.date_executed) return <span className="text-sm text-muted-foreground">-</span>;
        
        const date = moment(record.date_executed);
        return (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {date.format("MMM D, YYYY")}
            </div>
            <div className="text-xs text-muted-foreground">
              {date.format("h:mm A")}
            </div>
          </div>
        );
      },
    },
    {
      key: "qualitative_evaluation_results",
      header: "Score",
      width: "w-[15%]",
      render: (record: TestRunRequest) => {
        const results = record.qualitative_evaluation_results || [];
        if (results.length === 0) return <span className="text-sm text-muted-foreground">-</span>;

        const maxPossibleScore = results.length * 5; // Each result can score max 5
        const totalScore = results.reduce(
          (sum, result) => sum + (result.score_value || 0),
          0
        );
        const normalizedScore = Math.round((totalScore / maxPossibleScore) * 100);

        const getScoreColor = (score: number) => {
          // Using the same color system as TestRunDetail.tsx score distribution
          if (score >= 90) return {
            bg: "bg-[#52c41a]", // PERFECT - green
            text: "text-white",
            border: "border-[#52c41a]"
          };
          if (score >= 75) return {
            bg: "bg-[#b7eb8f]", // GREAT - light green  
            text: "text-green-800",
            border: "border-[#b7eb8f]"
          };
          if (score >= 60) return {
            bg: "bg-[#d9d9d9]", // GOOD - gray
            text: "text-gray-800", 
            border: "border-[#d9d9d9]"
          };
          if (score >= 40) return {
            bg: "bg-[#ffa39e]", // FAIR - light red
            text: "text-red-800",
            border: "border-[#ffa39e]"
          };
          return {
            bg: "bg-[#f5222d]", // POOR - red
            text: "text-white",
            border: "border-[#f5222d]"
          };
        };

        const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));
        const scoreColors = getScoreColor(normalizedScore);

        return (
          <ScoreTooltip score={normalizedScore} results={sortedResults}>
            <span 
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium cursor-help",
                scoreColors.bg,
                scoreColors.text,
                scoreColors.border
              )}
            >
              {normalizedScore}%
            </span>
          </ScoreTooltip>
        );
      },
    },
    {
      key: "id",
      header: "",
      width: "w-[55%]",
      render: (record: TestRunRequest) => (
        <div className="flex items-center justify-end gap-2">
          <RequestViewButton
            to={`/application/${
              record.original_request?.application_id
            }/session/${encodeURIComponent(
              record.original_request?.session_id
            )}/request/${record.original_request?.request_id}`}
            request={record.original_request}
            apiKey={apiKey}
            testSetId={testSetId}
            testSetRequestId={
              record.original_request?.metadata
                ?._maitai_test_request_id as string
            }
            systemPrompt={systemPrompt}
            buttonStyle={{ color: "#21b892" }}>
            Request
          </RequestViewButton>
          <ResponsesTooltip record={record}>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={(e) => e.stopPropagation()}>
              <DiffIcon className="w-3 h-3" />
              Responses
            </Button>
          </ResponsesTooltip>
          {intentGroupId && testSetId && (
            <Link
              to={`/intent-group/${intentGroupId}/testsets/${testSetId}/runs/${record.test_run_id}`}
              target="_blank">
              <Button variant="ghost" size="sm" className="gap-1">
                <ExternalLinkIcon className="w-3 h-3" />
                View Test Run
              </Button>
            </Link>
          )}
        </div>
      ),
    },
  ], [apiKey, testSetId, systemPrompt, intentGroupId]);

  const defaultSort = (a: TestRunRequest, b: TestRunRequest) => {
    // Default sort by date_executed descending
    const dateA = a.date_executed || 0;
    const dateB = b.date_executed || 0;
    return dateB - dateA;
  };

  return (
    <PaginatedTable
      data={data}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      serverSidePagination={true}
      showPagination={true}
      emptyTitle="No test run requests found"
      emptyDescription="Comparison data will appear here when test runs are available."
      onRowClick={onRowClick}
      defaultSort={defaultSort}
      loadingRows={5}
      rowClassName={(record) => {
        return record.test_run_id === testRunId &&
          record.id === testRunRequestId
          ? "bg-blue-50 dark:bg-blue-900/20" // Light blue background for the selected row
          : "";
      }}
    />
  );
};

export default TestRunRequestComparisonTable;
